import {
  LazyQueryResultTuple,
  OperationVariables,
  QueryResult,
} from '@apollo/client';
import { FilterList, Refresh, Search, ViewColumn } from '@mui/icons-material';
import {
  Box,
  Checkbox,
  CircularProgress,
  Collapse,
  FormControl,
  IconButton,
  InputLabel,
  ListItemText,
  Menu,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { Row } from '@tanstack/react-table';
import { debounce, filter, find, includes, map, omit } from 'lodash';
import React, {
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useSearchParams } from 'react-router-dom';

import { Pagination } from '../../components/pagination/pagination';
import { Button, type ButtonProps } from '../button/button';
import {
  NoResultsOverrideType,
  NoResultsText,
} from '../pagination-table/no-results-text';
import { PaginationResponse } from '../pagination-table/pagination-table';
import { Table } from '../table/table';
import { ColumnProps } from '../table/table-types';

export interface SearchFilter {
  label: string;
  value: string;
  type: 'select' | 'multi-select' | 'date-range' | 'checkbox';
  options?: { label: string; value: string }[];
  isActive?: boolean;
  selectedValue?: string;
  selectedValues?: string[];
}

// Breakpoint order for comparison
const BREAKPOINT_ORDER = ['xs', 'sm', 'md', 'lg', 'xl', 'xxl'] as const;

// Helper function to get visible columns based on current breakpoint
const getVisibleColumnsByBreakpoint = <
  TData,
  TQuery,
  TVars extends OperationVariables,
>(
  columns: ColumnProps<TData, TQuery, TVars>[],
  currentBreakpoint: string,
): string[] => {
  const currentBreakpointIndex = BREAKPOINT_ORDER.indexOf(
    currentBreakpoint as (typeof BREAKPOINT_ORDER)[number],
  );

  return columns
    .filter((column) => {
      // Always include columns without labels (button columns)
      if (!column.label) return false; // Don't include in the result array since we only want labeled columns

      // If no minimumBreakpoint is set, include the column
      if (!column.minimumBreakpoint) return true;

      // Include columns where current breakpoint is >= minimum required breakpoint
      const minBreakpointIndex = BREAKPOINT_ORDER.indexOf(
        column.minimumBreakpoint,
      );
      return currentBreakpointIndex >= minBreakpointIndex;
    })
    .map((column) => column.label!)
    .filter(Boolean); // Extra safety to remove any falsy values
};

/**
 * SearchPage component that combines search, filtering, sorting, and pagination
 * with a table display. Built to leverage the PaginationTable but with a custom
 * search interface that includes debounced search, sorting dropdown, and collapsible filters.
 * @example
 * ```tsx
 * <SearchPage
 *   dataAccessor={(data) => data.getUsers}
 *   columns={[
 *     { label: 'Name', field: 'name' },
 *     { label: 'Email', field: 'email' },
 *   ]}
 *   sortOptions={[
 *     { label: 'Name A-Z', value: SortEnum.NameAsc },
 *     { label: 'Name Z-A', value: SortEnum.NameDesc },
 *   ]}
 *   defaultSort={SortEnum.NameAsc}
 *   buildQuery={(search, filters, sort, page, perPage) => ({
 *     search,
 *     filters: filtersToObject(filters),
 *     sort,
 *     page,
 *     perPage,
 *   })}
 *   paginationQuery={useGetUsersLazyQuery}
 *   searchPlaceholder="Search users..."
 *   filters={[
 *     {
 *       label: 'Status',
 *       value: 'status',
 *       type: 'select',
 *       options: [
 *         { label: 'Active', value: 'active' },
 *         { label: 'Inactive', value: 'inactive' },
 *       ],
 *     },
 *   ]}
 * />
 * ```
 */
export interface SearchPageProps<
  TData,
  TAccessor extends PaginationResponse<TData>,
  TQuery,
  TVars extends OperationVariables,
  TSort,
> {
  // Table props
  actionButtons?: (t: QueryResult<TQuery, TVars>) => ButtonProps[];
  dataAccessor: (r: TQuery) => TAccessor | undefined | null;
  columns: ColumnProps<TData, TQuery, TVars>[];
  renderSubComponent?: (
    row: Row<TData>,
    operation?: QueryResult<TQuery, TVars>,
  ) => ReactNode;

  // Search and filtering props
  searchPlaceholder?: string;
  filters?: SearchFilter[];
  defaultFilters?: string[];
  sortOptions: { label: string; value: TSort }[];
  defaultSort: TSort;

  // Query building
  buildQuery: (
    searchValue: string,
    selectedFilters: SearchFilter[],
    sort: TSort,
    page: number,
    perPage: number,
  ) => TVars | undefined;
  paginationQuery: LazyQueryResultTuple<TQuery, TVars>;

  // UI options
  hideButtons?: boolean;
  hideSort?: boolean;
  persistentFilters?: boolean;
  enableSearchParams?: boolean;
  noResultsOverride?: NoResultsOverrideType;
  debounceMs?: number;
}

export function SearchPage<
  TData,
  TAccessor extends PaginationResponse<TData>,
  TQuery,
  TVars extends OperationVariables,
  TSort,
>({
  actionButtons = undefined,
  dataAccessor,
  buildQuery,
  columns,
  defaultFilters = [],
  defaultSort,
  filters = [],
  paginationQuery,
  renderSubComponent = undefined,
  hideButtons = false,
  hideSort = false,
  persistentFilters = false,
  enableSearchParams = true,
  noResultsOverride = undefined,
  searchPlaceholder = 'Search...',
  sortOptions,
  debounceMs = 300,
}: SearchPageProps<TData, TAccessor, TQuery, TVars, TSort>) {
  const [searchParams, setSearchParams] = useSearchParams();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // Detect current breakpoint using media queries
  const isXs = useMediaQuery(theme.breakpoints.only('xs'));
  const isSm = useMediaQuery(theme.breakpoints.only('sm'));
  const isMd = useMediaQuery(theme.breakpoints.only('md'));
  const isLg = useMediaQuery(theme.breakpoints.only('lg'));
  const isXl = useMediaQuery(theme.breakpoints.only('xl'));

  const currentBreakpoint = useMemo(() => {
    if (isXs) return 'xs';
    if (isSm) return 'sm';
    if (isMd) return 'md';
    if (isLg) return 'lg';
    if (isXl) return 'xl';
    return 'xxl';
  }, [isXs, isSm, isMd, isLg, isXl]);

  // Get initial visible columns based on current breakpoint
  const initialVisibleColumns = useMemo(
    () => getVisibleColumnsByBreakpoint(columns, currentBreakpoint),
    [columns, currentBreakpoint],
  );

  // Search state
  const [searchValue, setSearchValue] = useState<string>('');
  const [currentSort, setCurrentSort] = useState<TSort>(defaultSort);
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const [showFilters, setShowFilters] = useState<boolean>(false);
  const [selectedColumns, setSelectedColumns] = useState(initialVisibleColumns);
  const [columnMenuAnchor, setColumnMenuAnchor] = useState<null | HTMLElement>(
    null,
  );

  // Pagination state
  const [page, setPage] = useState<number>(1);
  const [perPage] = useState<number>(15);
  const [pageCount, setPageCount] = useState<number>(1);
  const [_itemCount, setItemCount] = useState<number>(0);

  // Filter state
  const [selectedFilters, setSelectedFilters] = useState<SearchFilter[]>(() =>
    map(filters, (f) => ({
      ...f,
      isActive:
        includes(defaultFilters, f.value) ||
        (!!f.selectedValues && f.selectedValues.length > 0) ||
        !!f.selectedValue,
    })),
  );

  const [loadData, operation] = paginationQuery;

  const accessor = useMemo(
    () => operation.data && dataAccessor(operation.data),
    [dataAccessor, operation.data],
  );

  const performSearch = useCallback(
    (
      searchTerm: string,
      searchFilters: SearchFilter[],
      sort: TSort,
      pageNum: number,
    ) => {
      const variables = buildQuery(
        searchTerm,
        searchFilters,
        sort,
        pageNum,
        perPage,
      );
      if (variables) {
        loadData({ variables });
        setPage(pageNum);

        // Update URL params if enabled
        if (enableSearchParams) {
          const newParams = new URLSearchParams();
          if (searchTerm) newParams.set('search', searchTerm);
          if (pageNum > 1) newParams.set('page', pageNum.toString());

          const activeFilters = filter(searchFilters, 'isActive').map(
            (f) => f.value,
          );
          if (activeFilters.length > 0) {
            newParams.set('filters', activeFilters.join(','));
          }

          setSearchParams(newParams);
        }
      }
    },
    [buildQuery, loadData, perPage, enableSearchParams, setSearchParams],
  );

  // Debounced search function
  const debouncedSearch = useRef(
    debounce((searchTerm: string) => {
      performSearch(searchTerm, selectedFilters, currentSort, 1);
    }, debounceMs),
  );

  // Update debounced function when dependencies change
  useEffect(() => {
    debouncedSearch.current = debounce((searchTerm: string) => {
      performSearch(searchTerm, selectedFilters, currentSort, 1);
    }, debounceMs);
  }, [selectedFilters, currentSort, debounceMs, performSearch]);

  // Handle search input change
  const handleSearchChange = useCallback((value: string) => {
    setSearchValue(value);
    debouncedSearch.current(value);
  }, []);

  // Handle sort change
  const handleSortChange = useCallback(
    (newSort: TSort) => {
      const newDirection =
        currentSort === newSort && sortDirection === 'asc' ? 'desc' : 'asc';
      setCurrentSort(newSort);
      setSortDirection(newDirection);
      performSearch(searchValue, selectedFilters, newSort, 1);
    },
    [currentSort, sortDirection, searchValue, selectedFilters, performSearch],
  );

  // Handle filter changes
  const handleFilterChange = useCallback(
    (updatedFilters: SearchFilter[]) => {
      setSelectedFilters(updatedFilters);
      performSearch(searchValue, updatedFilters, currentSort, 1);
    },
    [searchValue, currentSort, performSearch],
  );

  // Handle page change
  const handlePageChange = useCallback(
    (newPage: number) => {
      performSearch(searchValue, selectedFilters, currentSort, newPage);
    },
    [searchValue, selectedFilters, currentSort, performSearch],
  );

  // Update selected columns when breakpoint changes
  useEffect(() => {
    const visibleColumns = getVisibleColumnsByBreakpoint(
      columns,
      currentBreakpoint,
    );
    // Remove columns that shouldn't be visible at current breakpoint, but preserve user preferences
    setSelectedColumns((prev) =>
      prev.filter((col) => visibleColumns.includes(col)),
    );
  }, [currentBreakpoint, columns]);

  // Update pagination info when data changes
  useEffect(() => {
    if (accessor?.pageInfo) {
      setPageCount(accessor.pageInfo.pageCount || 1);
      setItemCount(accessor.pageInfo.itemCount || 0);
    }
  }, [accessor]);

  // Initialize from URL params and perform initial search
  useEffect(() => {
    let initialSearchValue = '';
    let initialPage = 1;
    let initialFilters = selectedFilters;

    if (enableSearchParams) {
      const searchParam = searchParams.get('search') || '';
      const pageParam = parseInt(searchParams.get('page') || '1', 10);
      const filtersParam = searchParams.get('filters')?.split(',') || [];

      if (searchParam) {
        setSearchValue(searchParam);
        initialSearchValue = searchParam;
      }
      if (pageParam > 1) {
        setPage(pageParam);
        initialPage = pageParam;
      }

      if (filtersParam.length > 0) {
        const updatedFilters = map(selectedFilters, (f) => ({
          ...f,
          isActive: includes(filtersParam, f.value),
        }));
        setSelectedFilters(updatedFilters);
        initialFilters = updatedFilters;
      }
    }

    // Perform initial search (this ensures we load data even with empty search)
    performSearch(initialSearchValue, initialFilters, defaultSort, initialPage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run on mount

  // Get active filter count
  const activeFilterCount = useMemo(
    () => filter(selectedFilters, 'isActive').length,
    [selectedFilters],
  );

  // Create cleaned columns without minimumBreakpoint for the table component
  const cleanedColumns: ColumnProps<TData, TQuery, TVars>[] = useMemo(
    () => map(columns, (column) => omit(column, 'minimumBreakpoint')),
    [columns],
  );

  // Filter columns based on selection
  const filteredColumns: ColumnProps<TData, TQuery, TVars>[] = useMemo(
    () =>
      filter(cleanedColumns, (c) => {
        // Always show button columns
        const isButtonColumn: boolean = !c.label;
        return (c.label && selectedColumns.includes(c.label)) || isButtonColumn;
      }),
    [cleanedColumns, selectedColumns],
  );

  // Handle refresh
  const handleRefresh = useCallback(() => {
    performSearch(searchValue, selectedFilters, currentSort, page);
  }, [performSearch, searchValue, selectedFilters, currentSort, page]);

  // Handle column menu
  const handleColumnMenuOpen = useCallback(
    (event: React.MouseEvent<HTMLElement>) => {
      setColumnMenuAnchor(event.currentTarget);
    },
    [],
  );

  const handleColumnMenuClose = useCallback(() => {
    setColumnMenuAnchor(null);
  }, []);

  const handleColumnToggle = useCallback((column: string) => {
    setSelectedColumns((prev) => {
      if (prev.includes(column)) {
        // Don't allow removing the last column
        if (prev.length === 1) return prev;
        return prev.filter((col) => col !== column);
      }
      return [...prev, column];
    });
  }, []);

  const renderFilterBar = () => {
    if (!filters.length) return null;

    return (
      <Collapse in={persistentFilters || showFilters}>
        <Paper elevation={0} sx={{ mt: 1 }}>
          <Stack direction="row" spacing={2} flexWrap="wrap" sx={{ mt: 2 }}>
            {filters.map((filterItem) => {
              const selectedFilter = find(selectedFilters, {
                value: filterItem.value,
              });

              if (filterItem.type === 'select' && filterItem.options) {
                return (
                  <Box key={filterItem.value} sx={{ minWidth: 200 }}>
                    <FormControl size="small" fullWidth>
                      <InputLabel>{filterItem.label}</InputLabel>
                      <Select
                        value={
                          (selectedFilter?.isActive &&
                            selectedFilter?.selectedValue) ||
                          ''
                        }
                        label={filterItem.label}
                        onChange={(event) => {
                          const value = event.target.value as string;
                          const updatedFilters = map(selectedFilters, (f) =>
                            f.value === filterItem.value
                              ? {
                                  ...f,
                                  isActive: !!value,
                                  selectedValue: value,
                                }
                              : f,
                          );
                          handleFilterChange(updatedFilters);
                        }}
                      >
                        <MenuItem value="">
                          <em>All</em>
                        </MenuItem>
                        {filterItem.options.map((option) => (
                          <MenuItem key={option.value} value={option.value}>
                            {option.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Box>
                );
              }

              if (filterItem.type === 'multi-select' && filterItem.options) {
                return (
                  <Box key={filterItem.value} sx={{ minWidth: 200 }}>
                    <FormControl size="small" fullWidth>
                      <InputLabel>{filterItem.label}</InputLabel>
                      <Select
                        multiple
                        value={selectedFilter?.selectedValues || []}
                        label={filterItem.label}
                        renderValue={(selected) => {
                          return (selected as string[])
                            .map((value) => {
                              const option = filterItem.options?.find(
                                (opt) => opt.value === value,
                              );
                              return option?.label || value;
                            })
                            .join(', ');
                        }}
                        onChange={(event) => {
                          const values = event.target.value as string[];
                          const updatedFilters = map(selectedFilters, (f) =>
                            f.value === filterItem.value
                              ? {
                                  ...f,
                                  isActive: values.length > 0,
                                  selectedValues: values,
                                }
                              : f,
                          );
                          handleFilterChange(updatedFilters);
                        }}
                      >
                        {filterItem.options.map((option) => {
                          const isSelected =
                            selectedFilter?.selectedValues?.includes(
                              option.value,
                            ) || false;
                          return (
                            <MenuItem key={option.value} value={option.value}>
                              <Checkbox checked={isSelected} size="small" />
                              <ListItemText primary={option.label} />
                            </MenuItem>
                          );
                        })}
                      </Select>
                    </FormControl>
                  </Box>
                );
              }

              if (filterItem.type === 'checkbox') {
                return (
                  <Box
                    key={filterItem.value}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      minWidth: 150,
                    }}
                  >
                    <Checkbox
                      checked={selectedFilter?.isActive || false}
                      onChange={(event) => {
                        const updatedFilters = map(selectedFilters, (f) =>
                          f.value === filterItem.value
                            ? { ...f, isActive: event.target.checked }
                            : f,
                        );
                        handleFilterChange(updatedFilters);
                      }}
                      size="small"
                    />
                    <Typography variant="body2">{filterItem.label}</Typography>
                  </Box>
                );
              }

              return null;
            })}
          </Stack>
        </Paper>
      </Collapse>
    );
  };

  return (
    <Paper>
      {/* Search Bar */}
      <Box sx={{ p: 2, mb: 2 }}>
        <Stack
          direction={hideSort ? 'row' : { xs: 'column', md: 'row' }}
          spacing={1}
        >
          {/* Search Input Row */}
          <TextField
            fullWidth
            size="small"
            placeholder={searchPlaceholder}
            value={searchValue}
            onChange={(e) => handleSearchChange(e.target.value)}
            InputProps={{
              startAdornment: (
                <Search
                  sx={{
                    marginRight: 1,
                    color: 'text.secondary',
                  }}
                />
              ),
            }}
          />

          {/* Controls Row */}
          <Stack direction="row" spacing={1} alignItems="center">
            {/* Sort Dropdown */}
            {!hideSort && (
              <FormControl size="small" sx={{ minWidth: 120, flex: 1 }}>
                <InputLabel>Sort</InputLabel>
                <Select
                  value={currentSort as string}
                  label="Sort"
                  onChange={(e) => handleSortChange(e.target.value as TSort)}
                >
                  {map(sortOptions, (option) => (
                    <MenuItem
                      key={option.value as string}
                      value={option.value as string}
                    >
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <span>{option.label}</span>
                      </Stack>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}

            {/* Refresh Button */}
            <IconButton onClick={handleRefresh} title="Refresh">
              {operation.loading || !operation.called ? (
                <CircularProgress size={16} />
              ) : (
                <Refresh />
              )}
            </IconButton>

            {/* Column Management */}
            <IconButton
              onClick={handleColumnMenuOpen}
              title="Manage Columns"
              size="small"
            >
              <ViewColumn />
            </IconButton>

            {/* Filter Toggle */}
            {filters.length > 0 && !persistentFilters && (
              <IconButton
                onClick={() => setShowFilters(!showFilters)}
                color={
                  showFilters || activeFilterCount > 0 ? 'primary' : 'default'
                }
                sx={{ position: 'relative' }}
              >
                <FilterList />
                {activeFilterCount > 0 && (
                  <Box
                    sx={{
                      position: 'absolute',
                      top: -2,
                      right: -2,
                      bgcolor: 'primary.main',
                      color: 'primary.contrastText',
                      borderRadius: '50%',
                      minWidth: 16,
                      height: 16,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '0.75rem',
                      fontWeight: 'bold',
                    }}
                  >
                    {activeFilterCount}
                  </Box>
                )}
              </IconButton>
            )}
          </Stack>
        </Stack>

        {/* Column Management Menu */}
        <Menu
          anchorEl={columnMenuAnchor}
          open={Boolean(columnMenuAnchor)}
          onClose={handleColumnMenuClose}
          PaperProps={{
            sx: { maxHeight: 300 },
          }}
        >
          {columns.map((column) => (
            <MenuItem
              key={column.label}
              onClick={() => handleColumnToggle(column.label!)}
              dense
            >
              <Checkbox
                checked={selectedColumns.includes(column.label!)}
                size="small"
              />
              <ListItemText primary={column.label} />
            </MenuItem>
          ))}
        </Menu>

        {/* Filter Bar */}
        {renderFilterBar()}
      </Box>

      {/* Results Table */}
      <Box>
        {operation.error && (
          <Box p={2}>
            <Typography color="error">
              Error loading results: {operation.error.message}
            </Typography>
          </Box>
        )}

        {/* Action Buttons */}
        {!hideButtons && actionButtons && (
          <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
            {map(actionButtons(operation), (buttonProps, index) => (
              <Button key={index} {...buttonProps} />
            ))}
          </Stack>
        )}

        {/* Table */}
        <Table<TData, TQuery, TVars>
          rows={accessor?.items || []}
          columns={filteredColumns.map((col) => ({ ...col, sortable: false }))}
          loading={operation.loading || !operation.called}
          renderSubComponent={
            renderSubComponent
              ? (row) => renderSubComponent(row, operation)
              : undefined
          }
        />

        {/* Pagination */}
        {pageCount > 1 && (
          <Box display="flex" justifyContent="center" mt={2}>
            <Pagination
              currentPage={page}
              pageCount={pageCount}
              setPage={handlePageChange}
            />
          </Box>
        )}

        {/* No Results */}
        {!operation.loading &&
          accessor &&
          (!accessor.items || accessor.items.length === 0) && (
            <NoResultsText textOverride={noResultsOverride} />
          )}
      </Box>
    </Paper>
  );
}
